import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "@ag-grid-community/core";
import { Colors } from "@blueprintjs/core";
import {
  createColDef,
  EVENT_TYPE_TO_DISPLAY,
  ItemGridFilterEventType,
  ItemGridFilterLayouts,
  ItemGridFilterLocations,
  ItemGridFilterPosts,
  ItemGridFilterProfiles,
} from "@package/ui-apollo-components";
import { ItemGridFilterDateTimeRange } from "@package/ui-apollo-components/src/components/ItemGridFilters/ItemGridFilterDateTimeRange";
import { useToaster } from "@package/ui-components";
import {
  type Event,
  type EventPayloadType,
} from "@package/ui-gql/index-analytics";
import {
  useGetLayoutListNameIdQuery,
  useGetLocationListNameIdQuery,
  useGetPostListNameIdQuery,
  useGetProfileListNameIdQuery,
} from "@package/ui-gql/index-data-access";
import { uniq } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ITEM_GRID_PATH_EDIT,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { useGetEvents } from "../../../../hooks/use-get-events";
import { useAppSelector } from "../../../../store/store";
import { ItemGrid } from "../../../ItemGrid/ItemGrid";
import { EventsGridToolbar } from "./EventsGridToolbar";

export function EventsGrid() {
  const { search } = useLocation();
  const { toaster } = useToaster();
  const navigate = useNavigate();
  const variables = useAppSelector((state) => state.itemGrid.events.variables);
  const variablesWithDateRange = useMemo(() => {
    let range = variables?.filter?.range;

    if (!range) {
      range = {
        end: moment().add(7, "days").toDate(),
      };
    }

    return {
      ...variables,
      filter: {
        ...variables?.filter,
        range,
      },
    };
  }, [variables]);

  const {
    data: dataEvents,
    loading: loadingEvents,
    error: errorEvents,
    refetch: refetchEvents,
  } = useGetEvents(variablesWithDateRange);

  const idsLocations = useMemo(
    () =>
      (uniq(
        dataEvents?.items?.map((e) => e.location).filter((e) => e),
      ) as string[]) ?? [],
    [dataEvents],
  );
  const idsProfiles = useMemo(
    () =>
      (uniq(
        dataEvents?.items?.map((e) => e.profile).filter((e) => e),
      ) as string[]) ?? [],
    [dataEvents],
  );
  const idsLayouts = useMemo(
    () =>
      (uniq(
        dataEvents?.items?.map((e) => e.layout).filter((e) => e),
      ) as string[]) ?? [],
    [dataEvents],
  );
  const idsPosts = useMemo(
    () =>
      (uniq(
        dataEvents?.items?.map((e) => e.post).filter((e) => e),
      ) as string[]) ?? [],
    [dataEvents],
  );

  const {
    data: dataLocations,
    loading: loadingLocations,
    error: errorLocations,
  } = useGetLocationListNameIdQuery({
    variables: {
      filter: {
        ids: idsLocations,
      },
      page: 0,
      perPage: 1000,
    },
    skip: !idsLocations?.length,
  });
  const {
    data: dataProfiles,
    loading: loadingProfiles,
    error: errorProfiles,
  } = useGetProfileListNameIdQuery({
    variables: {
      filter: {
        ids: idsProfiles,
      },
      page: 0,
      perPage: 1000,
    },
    skip: !idsProfiles?.length,
  });
  const {
    data: dataLayouts,
    loading: loadingLayouts,
    error: errorLayouts,
  } = useGetLayoutListNameIdQuery({
    variables: {
      filter: {
        ids: idsLayouts,
      },
      page: 0,
      perPage: 1000,
    },
    skip: !idsLayouts?.length,
  });
  const {
    data: dataPosts,
    loading: loadingPosts,
    error: errorPosts,
  } = useGetPostListNameIdQuery({
    variables: {
      filter: {
        ids: idsPosts,
      },
      page: 0,
      perPage: 1000,
    },
    skip: !idsPosts?.length,
  });

  const columnDefs = useMemo<ColDef[]>(
    () => [
      createColDef<Event>("timestamp", "Time", 0, 220),
      {
        ...createColDef<Event>("type", "Type", 0, 100),
        valueFormatter: (params: ValueFormatterParams<Event>) => {
          return params.value
            ? EVENT_TYPE_TO_DISPLAY[params.value as EventPayloadType]
            : "";
        },
      },
      {
        ...createColDef<Event>("location", "Location", 0, 150),
        cellRenderer: (params: ICellRendererParams<Event, string>) => {
          const location = dataLocations?.items?.find(
            (p) => p.id === params.value,
          );
          if (location) {
            return (
              <a
                style={{ color: Colors.BLUE1 }}
                onClick={() =>
                  navigate(
                    `/${SidebarEntryPath.locations}/${ITEM_GRID_PATH_EDIT}/${location.id}${search}`,
                  )
                }
              >
                {location.name}
              </a>
            );
          }

          return "";
        },
      },
      {
        ...createColDef<Event>("profile", "Profile", 0, 150),
        cellRenderer: (params: ICellRendererParams<Event, string>) => {
          const profile = dataProfiles?.items?.find(
            (p) => p.id === params.value,
          );
          if (profile) {
            return (
              <a
                style={{ color: Colors.BLUE1 }}
                onClick={() =>
                  navigate(
                    `/${SidebarEntryPath.profiles}/${ITEM_GRID_PATH_EDIT}/${profile.id}${search}`,
                  )
                }
              >
                {profile.name}
              </a>
            );
          }

          return "";
        },
      },
      {
        ...createColDef<Event>("post", "Content", 1, 150),
        cellRenderer: (params: ICellRendererParams<Event, string>) => {
          const post = dataPosts?.items?.find((p) => p.id === params.value);
          if (post) {
            return (
              <a
                style={{ color: Colors.BLUE1 }}
                onClick={() =>
                  navigate(
                    `/${SidebarEntryPath.posts}/${ITEM_GRID_PATH_EDIT}/${post.id}${search}`,
                  )
                }
              >
                {post.title}
              </a>
            );
          }

          return "";
        },
      },
      {
        ...createColDef<Event>("layout", "Layout", 0, 100),
        cellRenderer: (params: ICellRendererParams<Event, string>) => {
          const layout = dataLayouts?.items?.find((p) => p.id === params.value);
          if (layout) {
            return (
              <a
                style={{ color: Colors.BLUE1 }}
                onClick={() =>
                  navigate(
                    `/${SidebarEntryPath.layout}/${ITEM_GRID_PATH_EDIT}/${layout.id}${search}`,
                  )
                }
              >
                {layout.name}
              </a>
            );
          }

          return "";
        },
      },
    ],
    [
      dataLayouts?.items,
      dataLocations?.items,
      dataPosts?.items,
      dataProfiles?.items,
      navigate,
      search,
    ],
  );

  return (
    <div className="flex flex-col w-full h-full">
      <div className="shrink">
        <EventsGridToolbar
          onRefresh={async () => {
            const toastId = toaster?.show({
              message: "Fetching latest events...",
              intent: "none",
              icon: "refresh",
              timeout: 5000,
            });

            const result = await refetchEvents();

            toaster?.show(
              {
                message: result.error
                  ? "Failed fetching events"
                  : "Fetched latest events",
                intent: result?.error ? "danger" : "success",
                icon: result?.error ? "error" : "tick",
                timeout: 2000,
              },
              toastId,
            );
          }}
        />
        <div className="mt-4">
          <ItemGrid
            sidebarEntryPath={SidebarEntryPath.events}
            columnDefs={columnDefs}
            data={dataEvents}
            error={
              !!errorLocations ||
              !!errorProfiles ||
              !!errorLayouts ||
              !!errorPosts ||
              !!errorEvents
            }
            loading={
              loadingLocations ||
              loadingProfiles ||
              loadingLayouts ||
              loadingPosts ||
              loadingEvents
            }
            filtersSupported
            filterPanels={[
              <ItemGridFilterDateTimeRange key="dateRange" />,
              <ItemGridFilterEventType key="eventType" />,
              <ItemGridFilterLocations key="locations" />,
              <ItemGridFilterProfiles key="profiles" />,
              <ItemGridFilterPosts key="posts" />,
              <ItemGridFilterLayouts key="layouts" />,
            ]}
            pagination={{
              unlimited: true,
            }}
          />
        </div>
      </div>
    </div>
  );
}
